import { useEffect, useState } from "react";
import { api, idr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Package } from "lucide-react";

export default function Catalog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pOpen, setPOpen] = useState(false);
  const [cOpen, setCOpen] = useState(false);
  const [editP, setEditP] = useState(null);
  const [editC, setEditC] = useState(null);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const blankP = { name: "", series: "", brand: "", category: "", purchase_price: 0, stock: 0 };
  const [pForm, setPForm] = useState(blankP);
  const [cForm, setCForm] = useState({ name: "", default_margin_percent: 15 });

  const openNewP = () => { setEditP(null); setPForm(blankP); setPOpen(true); };
  const openEditP = (p) => { setEditP(p); setPForm({ ...p }); setPOpen(true); };
  const openNewC = () => { setEditC(null); setCForm({ name: "", default_margin_percent: 15 }); setCOpen(true); };
  const openEditC = (c) => { setEditC(c); setCForm({ ...c }); setCOpen(true); };

  const saveP = async () => {
    try {
      const body = { ...pForm, purchase_price: Number(pForm.purchase_price), stock: Number(pForm.stock) };
      if (editP) await api.put(`/products/${editP.id}`, body);
      else await api.post("/products", body);
      toast.success("Produk disimpan");
      setPOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveC = async () => {
    try {
      const body = { ...cForm, default_margin_percent: Number(cForm.default_margin_percent) };
      if (editC) await api.put(`/categories/${editC.id}`, body);
      else await api.post("/categories", body);
      toast.success("Kategori disimpan");
      setCOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delP = async (id) => { await api.delete(`/products/${id}`); toast.success("Produk dihapus"); load(); };
  const delC = async (id) => { await api.delete(`/categories/${id}`); toast.success("Kategori dihapus"); load(); };

  return (
    <div data-testid="catalog-page">
      <PageHeader overline="Data Master" title="Katalog Produk" />
      <div className="p-8">
        <Tabs defaultValue="products">
          <TabsList className="rounded-sm bg-secondary">
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-sm">Produk</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-sm">Kategori & Margin</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="add-product-btn" onClick={openNewP} className="rounded-sm">
                <Plus className="w-4 h-4 mr-1" /> Tambah Produk
              </Button>
            </div>
            <div className="border border-border rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary">
                  <tr className="text-left overline text-muted-foreground">
                    <th className="px-4 py-3">Produk</th><th className="px-4 py-3">Series</th>
                    <th className="px-4 py-3">Kategori</th><th className="px-4 py-3">Harga Beli</th><th className="px-4 py-3">Harga Jual (Zoho)</th>
                    <th className="px-4 py-3">Stok</th><th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {products.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada produk.</td></tr>
                  )}
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-secondary/40" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.brand}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{p.series || "-"}</td>
                      <td className="px-4 py-3">{p.category || "-"}</td>
                      <td className="px-4 py-3 font-mono">{idr(p.purchase_price)}</td>
                      <td className="px-4 py-3 font-mono" data-testid={`product-sale-${p.id}`}>{p.sale_price ? idr(p.sale_price) : "-"}</td>
                      <td className="px-4 py-3">{p.stock}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openEditP(p)} className="text-muted-foreground hover:text-primary mr-3" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        {isAdmin && <button onClick={() => delP(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="categories" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="add-category-btn" onClick={openNewC} className="rounded-sm">
                <Plus className="w-4 h-4 mr-1" /> Tambah Kategori
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((c) => (
                <div key={c.id} className="border border-border bg-card p-5 rounded-sm" data-testid={`category-card-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="overline text-muted-foreground mt-1">Margin Default</div>
                      <div className="font-heading font-black text-2xl text-accent">{c.default_margin_percent}%</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openEditC(c)} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                      {isAdmin && <button onClick={() => delC(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editP ? "Edit" : "Tambah"} Produk</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-muted-foreground">Nama Produk</Label>
              <Input data-testid="product-name-input" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground">Series</Label>
                <Input value={pForm.series} onChange={(e) => setPForm({ ...pForm, series: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Brand</Label>
                <Input value={pForm.brand} onChange={(e) => setPForm({ ...pForm, brand: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">Kategori</Label>
              <Select value={pForm.category} onValueChange={(v) => setPForm({ ...pForm, category: v })}>
                <SelectTrigger data-testid="product-category-select" className="mt-1 rounded-sm bg-secondary"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground">Harga Beli (IDR)</Label>
                <Input data-testid="product-price-input" type="number" value={pForm.purchase_price} onChange={(e) => setPForm({ ...pForm, purchase_price: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Stok</Label>
                <Input type="number" value={pForm.stock} onChange={(e) => setPForm({ ...pForm, stock: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-product-btn" onClick={saveP} className="rounded-sm">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editC ? "Edit" : "Tambah"} Kategori</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-muted-foreground">Nama Kategori</Label>
              <Input data-testid="category-name-input" value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            <div><Label className="overline text-muted-foreground">Margin Default (%)</Label>
              <Input data-testid="category-margin-input" type="number" value={cForm.default_margin_percent} onChange={(e) => setCForm({ ...cForm, default_margin_percent: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
          </div>
          <DialogFooter><Button data-testid="save-category-btn" onClick={saveC} className="rounded-sm">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
